import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import CreateAgeClassModal from '../../Modals/CreateAgeClassModal';
import EditAgeClassModal from '../../Modals/EditAgeClassModal';
import RecordDetailsModal from '../../shared/RecordDetailsModal';
import { ageClassApi } from '../../../../api/speciesLivestockRegistryApi';
import {
  actionMenuHtml,
  parseApiError,
  textCellHtml,
  truncateCellHtml,
} from './registryHelpers';
import RegistryToolbar from './RegistryToolbar';
import { useSpeciesCatalog } from './useSpeciesCatalog';
import SystemRowMenu, { standardRowItems, confirmAction } from '../../shared/SystemRowMenu';
import { anchorRect } from '../../../shared/useAnchoredMenu';

const COLUMNS = [
  { title: 'Species Code',          data: 'species_code',          sortable: true, render: textCellHtml },
  { title: 'Age Class Code',        data: 'age_class_code',        sortable: true, render: textCellHtml },
  { title: 'Age Class Label',       data: 'age_class_label',       sortable: true, render: textCellHtml },
  { title: 'Age Range Description', data: 'age_range_description', sortable: true, render: (value, type) => truncateCellHtml(value, type, 48) },
  { title: 'Sort Order',            data: 'sort_order',            sortable: true, render: textCellHtml },
  { title: 'Action', data: null, sortable: false, render: (_d, type) => (type === 'display' ? actionMenuHtml() : '') },
];

export default function AgeClassRegistry() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [error, setError] = useState('');
  const { species, error: speciesError, refresh: retrySpecies } = useSpeciesCatalog();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    ageClassApi.listAll({ per_page: 200 })
      .then(({ items }) => { if (active) setRecords(items); })
      .catch((err) => { if (active) setError(parseApiError(err, 'Failed to load age classes.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshTick]);

  const handleSearch = () => setSearchQuery(searchDraft.trim());
  const handleRefresh = () => setRefreshTick((v) => v + 1);

  const handleCreate = async (payload) => {
    await ageClassApi.create(payload);
    toast.success('Age class created.', { position: 'top-right' });
    handleRefresh();
  };

  const handleSave = async (payload) => {
    if (!payload?.uuid) throw new Error('Missing age class identifier.');
    await ageClassApi.update(payload.uuid, payload);
    toast.success('Age class updated.', { position: 'top-right' });
    handleRefresh();
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const label = row.age_class_label || row.age_class_code || 'this age class';
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete ${label}?`, confirmLabel: 'Delete', danger: true }))) return;
    setDeleting(true);
    try {
      await ageClassApi.remove(row.uuid);
      setViewRow(null);
      toast.success('Age class removed.', { position: 'top-right' });
      handleRefresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Unable to delete age class.'), { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'edit') setEditRow(row);
  };

  return (
    <section>
      <RegistryToolbar
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create New"
      />

      {speciesError && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{speciesError}</span>
            <button type="button" onClick={retrySpecies}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <button type="button" onClick={handleRefresh}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && (
        <p className="mb-3 text-sm text-gray-500">Loading age classes...</p>
      )}

      <NlimsDataTable
        tableId="age-class-table"
        columns={COLUMNS}
        data={records}
        serverSide={false}
        searchQuery={searchQuery}
        hideSearch
        pageLength={10}
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => { setEditRow(viewRow); setViewRow(null); }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Age Class Details"
        subtitle={viewRow?.age_class_label || viewRow?.age_class_code}
        fields={viewRow ? [
          { label: 'Species Code',          value: viewRow.species_code || '-' },
          { label: 'Age Class Code',        value: viewRow.age_class_code || '-' },
          { label: 'Age Class Label',       value: viewRow.age_class_label || '-' },
          { label: 'Sort Order',            value: viewRow.sort_order ?? '-' },
          { label: 'Age Range Description', value: viewRow.age_range_description || '-', wide: true },
          { label: 'UUID', value: viewRow.uuid, mono: true, wide: true },
        ] : []}
      />

      <CreateAgeClassModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        species={species}
      />

      <EditAgeClassModal
        isOpen={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        row={editRow}
        species={species}
      />
    </section>
  );
}
